import type {
  ClosureReason,
  OpenDetail,
  Pool,
  PoolSection,
  PoolStatus,
  ReopenEstimate,
  SeasonWindow,
  SectionStatus,
} from "../../shared/types";
import { CLOSED_SEASON_BADGE, CLOSED_SEASON_NOTE, SPLASH_CLOSURE_REASONS, SPLASH_OPEN_DETAILS, addressHtml, esc, favToggleHtml, footerHtml, openDetailPickerHtml, paintSectionStatus, reasonPickerHtml, reliabilityLabel, renderTimeline, reopenPickerHtml, sectionStatusView, slideView, slopedEntryBadgeHtml, statusLineHtml } from "../util";
import { cachedStatus, fetchStatus, loadPools } from "../store";
import { getRecentReports } from "../api";
import { enrichClosedReport, reportSection } from "../report";
import { dprHoursUrl } from "../dpr";
import { toggleFavorite } from "../favorites";
import { trackAction } from "../datadog";
import { CITY } from "../city";

/** " · Ward N" for cities that group by ward (DC); empty for Arlington. */
function wardBit(pool: Pool): string {
  return CITY.showWard ? ` · Ward ${pool.ward}` : "";
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * The "← All pools" back link. A splash pad is reached FROM the Splash tab, so
 * its detail page must go back there (`#/splash`) with the city's own splash
 * wording ("splash pads" / "spraygrounds") — sending a spray-park visitor to
 * the kiddie-pool list (which never lists spray parks) was a dead end. Pools —
 * and the pool-not-found fallback, where there is no `kind` to read — keep the
 * existing list link.
 */
function backLinkHtml(pool?: Pool): string {
  const splash = pool?.kind === "splash";
  const label = splash ? CITY.splashTabLabel.toLowerCase() : CITY.backToLabel;
  return `<a class="back" href="${splash ? "#/splash" : "#/"}">← All ${label}</a>`;
}

/** Splash pads get the splash footer explainer (see `footerHtml`). */
function detailFooter(pool: Pool): string {
  return footerHtml(pool.kind === "splash" ? "splash" : "pool");
}

function dprLink(poolId: string): string {
  const url = dprHoursUrl(poolId);
  if (!url) return "";
  return `<p class="dpr-link-row"><a class="dpr-link" href="${url}" target="_blank" rel="noopener noreferrer">DPR hours ↗</a></p>`;
}

/** Pick the season window that covers today, else the first one. */
function activeSeason(pool: Pool): SeasonWindow | undefined {
  const iso = new Date().toISOString().slice(0, 10);
  return pool.seasons.find((s) => s.start <= iso && iso <= s.end) ?? pool.seasons[0];
}

function hoursTable(season: SeasonWindow | undefined, section: PoolSection): string {
  if (!season) return '<p class="muted">No published hours.</p>';
  const schedule = section === "kiddie" ? season.kiddie : season.main;
  if (!schedule) return '<p class="muted">No published hours for this section.</p>';
  const rows = schedule.days
    .map((d, i) => {
      const hours = d ? `${esc(d.open)}–${esc(d.close)}` : "Closed";
      const cls = d ? "" : ' class="day-closed"';
      return `<tr${cls}><th scope="row">${DAY_NAMES[i]}</th><td>${hours}</td></tr>`;
    })
    .join("");
  return `<table class="hours"><tbody>${rows}</tbody></table>`;
}

function confidencePill(s: SectionStatus): string {
  if (s.reported == null) return "";
  const pct = Math.round(s.confidence * 100);
  return `
    <div class="confidence" title="Crowd confidence">
      <div class="confidence-bar"><span style="width:${pct}%"></span></div>
      <span class="confidence-label">${pct}% confident</span>
    </div>`;
}

function sectionBlock(pool: Pool, section: PoolSection, season: SeasonWindow | undefined): string {
  const isSplash = pool.kind === "splash";
  const label =
    section === "kiddie"
      ? "Children's pool"
      : isSplash
        ? "Splash pad"
        : "Main pool";
  return `
    <section class="detail-section" data-pool="${esc(pool.poolId)}" data-section="${section}">
      <div class="section-head">
        <h3 class="section-label">${label}</h3>
      </div>
      ${statusLineHtml()}
      <p class="reliability" data-role="reliability" hidden></p>
      <p class="hero" data-role="freshness"></p>
      <div data-role="confidence"></div>
      <div class="report-buttons">
        <button class="btn btn-open btn-lg" data-action="report" data-status="open">It's open</button>
        <button class="btn btn-closed btn-lg" data-action="report" data-status="closed">It's closed</button>
      </div>
      ${isSplash ? reasonPickerHtml(SPLASH_CLOSURE_REASONS) : reasonPickerHtml()}
      ${isSplash ? "" : reopenPickerHtml()}
      ${openDetailPickerHtml(isSplash ? SPLASH_OPEN_DETAILS : undefined)}
      <details class="hours-wrap" open>
        <summary>${CITY.id === "dc" ? "Published DPR hours" : "Published schedule"}</summary>
        ${hoursTable(season, section)}
      </details>
      <div class="history" data-role="history">
        <h4 class="history-title">Recent reports</h4>
        <ol class="timeline" data-role="timeline"><li class="muted">Loading…</li></ol>
      </div>
    </section>`;
}

/**
 * The water-slide section on a waterslide pool's detail page (Deanwood & Barry
 * Farm). Mirrors the List card's slide control and is now at parity with the
 * kiddie/main sections: defaults Off, "Slide off" reveals the shared reason +
 * reopen pickers, and the section carries a recent-reports history timeline (it
 * has no DPR hours table, as the slide has no published schedule).
 *
 * Exception: a closed-for-season pool's slide is informational only — a fixed
 * "Off · closed for season" badge with disabled (un-wired) buttons.
 */
function slideBlock(pool: Pool): string {
  if (pool.closedForSeason) {
    return `
    <section class="detail-section section-slide sec-acc-neutral" data-pool="${esc(pool.poolId)}" data-section="slide">
      <div class="section-head">
        <h3 class="section-label">🛝 Water slide</h3>
      </div>
      <p class="status-line"><span class="status status-neutral" data-role="status">Off</span> <span class="qual" data-role="qual">· closed for season</span></p>
      <div class="report-buttons">
        <button class="btn btn-open btn-lg" disabled>Running</button>
        <button class="btn btn-closed btn-lg" disabled>Closed for the season</button>
      </div>
    </section>`;
  }
  return `
    <section class="detail-section section-slide sec-acc-neutral" data-pool="${esc(pool.poolId)}" data-section="slide">
      <div class="section-head">
        <h3 class="section-label">🛝 Water slide</h3>
      </div>
      <p class="status-line"><span class="status status-neutral" data-role="status">Off</span> <span class="qual" data-role="qual">· usually off</span></p>
      <p class="hero" data-role="freshness">Tap if it's running</p>
      <div class="report-buttons">
        <button class="btn btn-open btn-lg" data-action="report" data-status="open">Running</button>
        <button class="btn btn-closed btn-lg" data-action="report" data-status="closed">Off</button>
      </div>
      ${reasonPickerHtml()}
      ${reopenPickerHtml()}
      <div class="history" data-role="history">
        <h4 class="history-title">Recent reports</h4>
        <ol class="timeline" data-role="timeline"><li class="muted">Loading…</li></ol>
      </div>
    </section>`;
}

function applySectionStatus(root: HTMLElement, poolId: string, s: SectionStatus) {
  const block = root.querySelector<HTMLElement>(
    `.detail-section[data-pool="${CSS.escape(poolId)}"][data-section="${s.section}"]`,
  );
  if (!block) return;
  // The slide reads Off/Running (defaults Off); other sections use the
  // confidence-led view. paintSectionStatus fills word/qual/hero/accent and the
  // report-button labels, keeping the data-role hooks stable.
  paintSectionStatus(block, s.section === "slide" ? slideView(s) : sectionStatusView(s));
  // The slide block has no confidence row, so guard it.
  const conf = block.querySelector<HTMLElement>('[data-role="confidence"]');
  if (conf) conf.innerHTML = confidencePill(s);
}

function applyStatus(root: HTMLElement, status: PoolStatus) {
  for (const s of status.sections) applySectionStatus(root, status.poolId, s);
  // Per-pool reliability label (the detail view renders only the kiddie section,
  // which is the section reliability is computed for).
  const rel = root.querySelector<HTMLElement>('[data-role="reliability"]');
  if (rel) {
    const label = reliabilityLabel(status.reliability);
    rel.textContent = label ?? "";
    rel.hidden = !label;
  }
}

function loadHistory(root: HTMLElement, poolId: string, section: PoolSection) {
  const list = root.querySelector<HTMLElement>(
    `.detail-section[data-pool="${CSS.escape(poolId)}"][data-section="${section}"] .history [data-role="timeline"]`,
  );
  if (!list) return;
  getRecentReports(poolId, section)
    .then((res) => renderTimeline(list, res.reports))
    .catch(() => {
      list.innerHTML = '<li class="muted">History unavailable offline.</li>';
    });
}

export async function renderDetail(app: HTMLElement, poolId: string): Promise<void> {
  const { pools, live } = await loadPools();
  const pool = pools.find((p) => p.poolId === poolId);

  if (!pool) {
    app.innerHTML = `
      <header class="topbar">
        ${backLinkHtml()}
      </header>
      <main><p class="empty">Pool not found.</p></main>`;
    return;
  }

  // Closed for the whole season (renovation): no status, hours, or reporting —
  // just a clear notice. Render and return before the reportable detail UI.
  if (pool.closedForSeason) {
    app.innerHTML = `
      <header class="topbar">
        ${backLinkHtml(pool)}
        <div class="card-head"><h1>${esc(pool.name)}</h1>${favToggleHtml(poolId)}</div>
        <p class="addr">${addressHtml(pool.address)}${wardBit(pool)}</p>
      </header>
      <main class="detail">
        <section class="detail-section detail-section-closed">
          <div class="section-head">
            <span class="badge badge-unknown">${CLOSED_SEASON_BADGE}</span>
          </div>
          <p class="closed-season-note">${CLOSED_SEASON_NOTE}</p>
        </section>
        ${CITY.features.slides && pool.hasWaterslide ? slideBlock(pool) : ""}
      </main>
      ${detailFooter(pool)}`;
    const closedFav = app.querySelector<HTMLButtonElement>('[data-action="favorite"]');
    closedFav?.addEventListener("click", () => {
      const fav = toggleFavorite(poolId);
      closedFav.classList.toggle("is-fav", fav);
      closedFav.setAttribute("aria-pressed", String(fav));
      const label = fav ? "Remove favorite" : "Favorite";
      closedFav.setAttribute("aria-label", label);
      closedFav.setAttribute("title", label);
      trackAction("favorite_toggle", { poolId, nowFavorited: fav });
    });
    // A closed-for-season pool's slide is informational only — `slideBlock`
    // renders it Off + disabled with no `data-action`, so there is nothing to
    // wire (and no status to fetch); the section just tells families it's out.
    return;
  }

  const season = activeSeason(pool);
  // Kiddie pools render BOTH the Main section and the Children's section, Main
  // first (the facility has the big pool too). Splash pads and main-only pools
  // (Hearst, Langdon Park) render just the reportable `main` section, so we never
  // surface a "Children's pool" block that doesn't exist on the actual facility.
  // A `childrenOnly` pool's whole basin IS the kiddie section, so it renders just
  // the Children's section (no Main).
  const sections: PoolSection[] =
    pool.kind === "splash"
      ? ["main"]
      : pool.childrenOnly
        ? ["kiddie"]
        : pool.hasKiddiePool
          ? ["main", "kiddie"]
          : ["main"];

  app.innerHTML = `
    <header class="topbar">
      ${backLinkHtml(pool)}
      <div class="card-head"><h1>${esc(pool.name)}</h1>${favToggleHtml(poolId)}</div>
      <p class="addr">${addressHtml(pool.address)}${wardBit(pool)}${pool.indoor ? " · Indoor" : ""}</p>
      ${pool.slopedEntry ? `<p class="sloped-entry">${slopedEntryBadgeHtml()}</p>` : ""}
      ${dprLink(poolId)}
      ${live ? "" : '<p class="dev-banner">Showing sample data. Backend not connected yet.</p>'}
    </header>
    <main class="detail">
      ${sections.map((s) => sectionBlock(pool, s, season)).join("")}
      ${CITY.features.slides && pool.hasWaterslide ? slideBlock(pool) : ""}
    </main>
    ${detailFooter(pool)}`;

  // Favorite toggle: flip persisted state and reflect it in the button so the
  // list view sees it (and sorts this pool up) on the way back.
  const favBtn = app.querySelector<HTMLButtonElement>('[data-action="favorite"]');
  favBtn?.addEventListener("click", () => {
    const fav = toggleFavorite(poolId);
    favBtn.classList.toggle("is-fav", fav);
    favBtn.setAttribute("aria-pressed", String(fav));
    const label = fav ? "Remove favorite" : "Favorite";
    favBtn.setAttribute("aria-label", label);
    favBtn.setAttribute("title", label);
    trackAction("favorite_toggle", { poolId, nowFavorited: fav });
  });

  // Wire report buttons (optimistic, refreshes status + timeline on success).
  // "Mark as Closed" reveals the inline reason picker first; the closed report
  // is submitted when a reason chip (or "Skip") is tapped, so the one-tap path
  // survives via "Skip".
  app.querySelectorAll<HTMLButtonElement>('[data-action="report"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const block = btn.closest<HTMLElement>(".detail-section")!;
      const sectionName = block.dataset.section as PoolSection;
      const status = btn.dataset.status as "open" | "closed";
      const badge = block.querySelector<HTMLElement>('[data-role="status"]')!;
      const picker = block.querySelector<HTMLElement>('[data-role="reason-picker"]');
      if (status === "closed" && picker) {
        // Commit the closed status on the FIRST tap (durable even if the user
        // abandons the follow-up), THEN reveal the reason picker. Stash the
        // written row's ts so the reopen chip enriches that exact row.
        void reportSection(poolId, sectionName, "closed", badge, (s) => {
          applyStatus(app, s);
          loadHistory(app, poolId, sectionName);
        }).then((ts) => {
          if (ts === undefined) return;
          block.dataset.reportTs = String(ts);
          picker.hidden = false;
        });
        return;
      }
      if (picker) picker.hidden = true;
      void reportSection(poolId, sectionName, status, badge, (s) => {
        applyStatus(app, s);
        loadHistory(app, poolId, sectionName);
      });
      // After the plain open report fires, reveal the optional detail add-on.
      if (status === "open") {
        const detailPicker = block.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
        if (detailPicker) detailPicker.hidden = false;
      }
    });
  });

  // Open-detail chips: send a SECOND open report carrying the chosen detail
  // (rate-limit-exempt server-side), then collapse the picker. "Skip" (empty
  // data-detail) just collapses it — the plain open report already landed.
  app.querySelectorAll<HTMLButtonElement>('[data-role="open-detail-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const block = chip.closest<HTMLElement>(".detail-section")!;
      const sectionName = block.dataset.section as PoolSection;
      const badge = block.querySelector<HTMLElement>('[data-role="status"]')!;
      const detailPicker = block.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
      const detail = (chip.dataset.detail || undefined) as OpenDetail | undefined;
      if (detailPicker) detailPicker.hidden = true;
      if (!detail) return;
      void reportSection(poolId, sectionName, "open", badge, (s) => {
        applyStatus(app, s);
        loadHistory(app, poolId, sectionName);
      }, undefined, undefined, detail);
    });
  });

  // Reason chips: stash the chosen reason (empty for "Skip" → no reason) on the
  // block, collapse the reason picker, and reveal the reopen-estimate row. Submit
  // happens on a reopen chip — preserving one-tap-closed via reason-Skip+reopen-Skip.
  //
  // A SPLASH pad renders no reopen row at all (the pads are unstaffed, so nobody
  // there can estimate a reopen time). With no second row to advance to, the
  // reason chip is the last step: it enriches the already-committed closed
  // report right away.
  app.querySelectorAll<HTMLButtonElement>('[data-role="reason-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const block = chip.closest<HTMLElement>(".detail-section")!;
      block.dataset.pendingReason = chip.dataset.reason || "";
      const reasonPicker = block.querySelector<HTMLElement>('[data-role="reason-picker"]');
      const reopenPicker = block.querySelector<HTMLElement>('[data-role="reopen-picker"]');
      if (reasonPicker) reasonPicker.hidden = true;
      if (reopenPicker) {
        reopenPicker.hidden = false;
        return;
      }
      const sectionName = block.dataset.section as PoolSection;
      const reason = (chip.dataset.reason || undefined) as ClosureReason | undefined;
      const targetTs = Number(block.dataset.reportTs);
      if (reason && Number.isFinite(targetTs)) {
        void enrichClosedReport(poolId, sectionName, targetTs, (s) => {
          applyStatus(app, s);
          loadHistory(app, poolId, sectionName);
        }, reason);
      }
    });
  });

  // Reopen chips: ENRICH the already-committed closed report (stashed reportTs)
  // with the stashed reason + the chosen reopen estimate — no new report, no
  // rollup. Skip+Skip (both empty) sends nothing; the closed report already landed.
  app.querySelectorAll<HTMLButtonElement>('[data-role="reopen-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const block = chip.closest<HTMLElement>(".detail-section")!;
      const sectionName = block.dataset.section as PoolSection;
      const reopenPicker = block.querySelector<HTMLElement>('[data-role="reopen-picker"]');
      const reason = (block.dataset.pendingReason || undefined) as ClosureReason | undefined;
      const reopen = (chip.dataset.reopen || undefined) as ReopenEstimate | undefined;
      if (reopenPicker) reopenPicker.hidden = true;
      const targetTs = Number(block.dataset.reportTs);
      if (Number.isFinite(targetTs)) {
        void enrichClosedReport(poolId, sectionName, targetTs, (s) => {
          applyStatus(app, s);
          loadHistory(app, poolId, sectionName);
        }, reason, reopen);
      }
    });
  });

  // Status: paint cached immediately, then refresh from the API.
  const cached = cachedStatus(poolId);
  if (cached) applyStatus(app, cached);
  if (live) {
    fetchStatus(poolId).then((s) => {
      if (s) applyStatus(app, s);
    });
  }

  // Report-history timelines per section. The water slide now has its own
  // history (report parity), so include it when present.
  const historySections: PoolSection[] = pool.hasWaterslide ? [...sections, "slide"] : sections;
  if (live) {
    for (const s of historySections) loadHistory(app, poolId, s);
  } else {
    for (const s of historySections) {
      const block = app.querySelector<HTMLElement>(
        `.detail-section[data-pool="${CSS.escape(poolId)}"][data-section="${s}"] .history`,
      );
      const list = block?.querySelector<HTMLElement>('[data-role="timeline"]');
      if (list) list.innerHTML = '<li class="muted">History unavailable offline.</li>';
    }
  }
}
